import { supportsUsagePauseThreshold } from "@better-ccflare/core";
import { AccountPresenter } from "@better-ccflare/ui-common";
import {
	AlertCircle,
	Edit2,
	Gauge,
	Globe,
	Hash,
	KeyRound,
	Pause,
	Play,
	RefreshCw,
	Replace,
	Trash2,
	Zap,
} from "lucide-react";
import { useState } from "react";
import type { Account } from "../../api";
import {
	providerShowsCreditsBalance,
	providerShowsWeeklyUsage,
	providerSupportsAutoFeatures,
	providerSupportsCustomBilling,
} from "../../utils/provider-utils";
import { OAuthTokenStatusWithBoundary } from "../OAuthTokenStatus";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { RateLimitProgress } from "./RateLimitProgress";

function formatTokenCount(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
	return String(n);
}

function formatPauseReason(reason: string | null): string | null {
	if (!reason) return null;
	return reason.replaceAll("_", " ");
}

interface AccountListItemProps {
	account: Account;
	isPrimary?: boolean;
	onPauseToggle: (account: Account) => void;
	onForceResetRateLimit: (account: Account) => void;
	onRefreshUsage: (account: Account) => Promise<void>;
	onRemove: (account: Account) => void;
	onRename: (account: Account) => void;
	onPriorityChange: (account: Account) => void;
	onAutoFallbackToggle: (account: Account) => void;
	onAutoRefreshToggle: (account: Account) => void;
	onBillingTypeToggle: (account: Account) => void;
	onAutoPauseOnOverageToggle?: (account: Account) => void;
	onPeakHoursPauseToggle?: (account: Account) => void;
	onUsageThresholdsChange?: (account: Account) => void;
	onCustomEndpointChange?: (account: Account) => void;
	onModelMappingsChange?: (account: Account) => void;
	onRequestTransformerChange?: (account: Account) => void;
	onReauth?: (account: Account) => void;
	onAnthropicReauth?: (account: Account) => void;
	onCodexReauth?: (account: Account) => void;
}

export function AccountListItem({
	account,
	isPrimary = false,
	onPauseToggle,
	onForceResetRateLimit,
	onRefreshUsage,
	onRemove,
	onRename,
	onPriorityChange,
	onAutoFallbackToggle,
	onAutoRefreshToggle,
	onBillingTypeToggle,
	onAutoPauseOnOverageToggle,
	onPeakHoursPauseToggle,
	onUsageThresholdsChange,
	onCustomEndpointChange,
	onModelMappingsChange,
	onRequestTransformerChange,
	onReauth,
	onAnthropicReauth,
	onCodexReauth,
}: AccountListItemProps) {
	const [isRefreshingUsage, setIsRefreshingUsage] = useState(false);
	const presenter = new AccountPresenter(account);
	// Only hard-limit statuses mean the account is actually blocked; soft warnings
	// like "allowed_warning" / "queueing_soft" mean the account is still usable.
	const HARD_LIMIT_PREFIXES = [
		"rate_limited",
		"blocked",
		"queueing_hard",
		"payment_required",
	];
	const isHardLimited = HARD_LIMIT_PREFIXES.some((prefix) =>
		presenter.rateLimitStatus.toLowerCase().startsWith(prefix),
	);
	// Also show Force Reset when rate_limited_until is in the future even if
	// rate_limit_status is soft/OK — the selector still skips the account.
	const isBlockedByLegacyLock =
		typeof account.rateLimitedUntil === "number" &&
		account.rateLimitedUntil > Date.now();
	const showForceReset =
		(isHardLimited || isBlockedByLegacyLock) && !presenter.isPaused;
	// staleLockDetected only fires when numeric usage data exists (Anthropic accounts);
	// Zai/NanoGPT accounts have usageUtilization === null and are correctly excluded
	const staleLockDetected =
		showForceReset &&
		typeof account.usageUtilization === "number" &&
		account.usageUtilization < 100;
	const isUsageThrottled =
		typeof account.usageThrottledUntil === "number" &&
		account.usageThrottledUntil > Date.now();

	// Parse Bedrock profile and region from custom_endpoint
	let bedrockProfile: string | null = null;
	let bedrockRegion: string | null = null;
	let bedrockCrossRegionMode: string | null = null;
	if (account.provider === "bedrock" && account.customEndpoint) {
		const match = account.customEndpoint.match(/^bedrock:([^:]+):(.+)$/);
		if (match) {
			bedrockProfile = match[1];
			bedrockRegion = match[2];
		}
		bedrockCrossRegionMode = account.crossRegionMode || "geographic";
	}

	const activeUsageThresholds = [
		account.usagePauseFiveHourEnabled && account.usagePauseFiveHourThreshold
			? `${account.usagePauseFiveHourThreshold}% of the 5-hour window`
			: null,
		account.usagePauseWeeklyEnabled && account.usagePauseWeeklyThreshold
			? `${account.usagePauseWeeklyThreshold}% of the weekly window`
			: null,
	].filter((entry): entry is string => entry !== null);
	const hasUsageThreshold = activeUsageThresholds.length > 0;

	return (
		<div
			className={`p-4 border rounded-lg transition-colors space-y-4 ${
				isPrimary
					? "border-primary bg-primary/5 shadow-sm"
					: "border-border hover:border-muted-foreground/50"
			}`}
		>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<div className="flex items-center gap-2">
							<p className="font-medium">{account.name}</p>
							{isPrimary && (
								<span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
									Primary
								</span>
							)}
							<span className="px-2 py-0.5 text-xs font-medium bg-secondary text-secondary-foreground rounded-full">
								Priority: {account.priority}
							</span>
							<OAuthTokenStatusWithBoundary
								accountName={account.name}
								hasRefreshToken={account.hasRefreshToken}
							/>
							{providerSupportsAutoFeatures(account.provider) && (
								<>
									<div className="flex items-center gap-2">
										<span className="text-xs text-muted-foreground">
											Auto-fallback:
										</span>
										<Switch
											checked={account.autoFallbackEnabled}
											onCheckedChange={() => onAutoFallbackToggle(account)}
											title="Automatically switch back to this account from lower-priority ones when its rate limit resets. Requires multiple accounts with different priorities."
										/>
									</div>
									<div className="flex items-center gap-2">
										<span className="text-xs text-muted-foreground">
											Auto-refresh:
										</span>
										<Switch
											checked={account.autoRefreshEnabled}
											onCheckedChange={() => onAutoRefreshToggle(account)}
											title="Automatically sends a minimal message when the usage window resets to avoid cold-start latency. Does not affect OAuth token refreshing."
										/>
									</div>
								</>
							)}
							{providerSupportsCustomBilling(account.provider) && (
								<div className="flex items-center gap-2">
									<span className="text-xs text-muted-foreground">
										Plan billing:
									</span>
									<Switch
										checked={account.billingType === "plan"}
										onCheckedChange={() => onBillingTypeToggle(account)}
										title="Toggle plan billing for this account"
									/>
								</div>
							)}
							{account.provider === "anthropic" &&
								onAutoPauseOnOverageToggle && (
									<div className="flex items-center gap-2">
										<span className="text-xs text-muted-foreground">
											Auto-pause on overage:
										</span>
										<Switch
											checked={account.autoPauseOnOverageEnabled ?? false}
											onCheckedChange={() =>
												onAutoPauseOnOverageToggle(account)
											}
											title="Automatically pause account when overage usage is detected. Note: detection only happens when Anthropic API reports overage, so some overage usage may occur before pausing. Account resumes when usage window resets."
										/>
									</div>
								)}
							{(account.provider === "zai" ||
								account.provider === "deepseek") &&
								onPeakHoursPauseToggle && (
									<div className="flex items-center gap-2">
										<span className="text-xs text-muted-foreground">
											Peak hours pause:
										</span>
										<Switch
											checked={account.peakHoursPauseEnabled ?? false}
											onCheckedChange={() => onPeakHoursPauseToggle(account)}
											title={
												account.provider === "deepseek"
													? "Automatically pause this account during DeepSeek peak hours (01:00–04:00 and 06:00–10:00 UTC, weekdays, excluding Chinese public holidays)"
													: "Automatically pause this account during Zai peak hours (14:00–18:00 SGT)"
											}
										/>
									</div>
								)}
						</div>
						<div className="flex items-center gap-2">
							<p className="text-sm text-muted-foreground">
								{account.provider}
							</p>
							{account.provider === "bedrock" && bedrockProfile && (
								<>
									<span className="text-sm text-muted-foreground">•</span>
									<p className="text-sm text-muted-foreground">
										Profile: {bedrockProfile}
									</p>
									{bedrockRegion && (
										<>
											<span className="text-sm text-muted-foreground">•</span>
											<div
												className="flex items-center gap-1"
												title={`Region: ${bedrockRegion}`}
											>
												<Globe className="h-3 w-3 text-muted-foreground" />
												<p className="text-sm text-muted-foreground">
													{bedrockRegion}
												</p>
											</div>
										</>
									)}
									{bedrockCrossRegionMode && (
										<>
											<span className="text-sm text-muted-foreground">•</span>
											<p
												className="text-sm text-muted-foreground"
												title="Cross-region inference mode"
											>
												{bedrockCrossRegionMode}
											</p>
										</>
									)}
								</>
							)}
						</div>
					</div>
					<div className="flex items-center gap-2">
						{presenter.isRateLimited && (
							<span title="Account is rate-limited - requests will be rejected until the limit resets">
								<AlertCircle className="h-4 w-4 text-yellow-600" />
							</span>
						)}
						<span className="text-sm">{presenter.requestCount} requests</span>
						<span className="text-sm text-muted-foreground">
							{presenter.sessionInfo}
						</span>
						{account.requiresReauth ? (
							<Badge
								variant="destructive"
								title="Refresh token invalid — re-authenticate"
							>
								Needs authentication
							</Badge>
						) : (
							presenter.isPaused && (
								<span className="text-sm text-muted-foreground">
									Paused
									{formatPauseReason(account.pauseReason)
										? ` (${formatPauseReason(account.pauseReason)})`
										: ""}
								</span>
							)
						)}
						{!account.requiresReauth &&
							(account.reauthDeadlineStatus === "warning" ||
								account.reauthDeadlineStatus === "critical" ||
								account.reauthDeadlineStatus === "expired") && (
								<span
									className="text-sm text-amber-600"
									title={`Empirically observed ~28-day OAuth reauthentication deadline. Run: bun run cli --reauthenticate "${account.name}"`}
								>
									{account.reauthDeadlineStatus === "expired"
										? Math.abs(account.hoursUntilReauthRequired ?? 0) < 24
											? `Reauth overdue by ${Math.abs(account.hoursUntilReauthRequired ?? 0)}h`
											: `Reauth overdue by ${Math.abs(account.daysUntilReauthRequired ?? 0)}d`
										: (account.hoursUntilReauthRequired ?? Infinity) < 24
											? `Reauth in ${account.hoursUntilReauthRequired}h`
											: `Reauth in ${account.daysUntilReauthRequired}d`}
								</span>
							)}
						{!presenter.isPaused && presenter.rateLimitStatus !== "OK" && (
							<span
								className={`text-sm ${
									presenter.rateLimitStatus
										.toLowerCase()
										.startsWith("allowed_warning")
										? "text-amber-600"
										: presenter.rateLimitStatus
													.toLowerCase()
													.startsWith("allowed")
											? "text-green-600"
											: "text-destructive"
								}`}
							>
								{presenter.rateLimitStatus}
							</span>
						)}
						{staleLockDetected && (
							<span
								className="text-sm text-amber-600"
								title="Stale lock detected: usage data shows available capacity but account is still rate-limited"
							>
								Stale lock detected
							</span>
						)}
						{isUsageThrottled && (
							<span
								className="text-sm text-amber-600"
								title="Usage throttling is delaying requests for this account until pacing catches up"
							>
								Usage throttled
							</span>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onRename(account)}
						title="Rename account"
					>
						<Edit2 className="h-4 w-4" />
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onPriorityChange(account)}
						title="Change account priority"
					>
						<Zap className="h-4 w-4" />
					</Button>
					{onCustomEndpointChange && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onCustomEndpointChange(account)}
							title={
								account.customEndpoint
									? `Custom endpoint: ${account.customEndpoint}`
									: "Set custom endpoint"
							}
						>
							<Globe
								className={`h-4 w-4 ${
									account.customEndpoint ? "text-primary" : ""
								}`}
							/>
						</Button>
					)}
					{supportsUsagePauseThreshold(account.provider) &&
						onUsageThresholdsChange && (
							<Button
								variant="ghost"
								size="sm"
								// The tint goes on the button, not on the icon: this theme
								// sets --accent to the same orange as --primary, so a
								// primary-coloured icon disappears into the ghost button's
								// hover background and the control reads as a solid orange
								// square. On the button, the hover's accent-foreground wins.
								className={
									hasUsageThreshold
										? "text-primary hover:text-accent-foreground"
										: ""
								}
								onClick={() => onUsageThresholdsChange(account)}
								title={
									hasUsageThreshold
										? `Pauses at ${activeUsageThresholds.join(", ")}`
										: "Set usage pause thresholds"
								}
							>
								<Gauge className="h-4 w-4" />
							</Button>
						)}
					{onModelMappingsChange && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onModelMappingsChange(account)}
							title={
								account.modelMappings
									? `Model mappings configured (${Object.keys(account.modelMappings).length} mappings)`
									: "Configure model mappings"
							}
						>
							<Hash
								className={`h-4 w-4 ${
									account.modelMappings ? "text-primary" : ""
								}`}
							/>
						</Button>
					)}
					{account.provider === "openai-compatible" &&
						onRequestTransformerChange && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onRequestTransformerChange(account)}
								aria-label="Configure request transformer"
								aria-pressed={account.requestTransformer !== null}
								title={
									account.requestTransformer
										? "Request transformer: Max Tokens → Max Completion Tokens"
										: "Configure request transformer"
								}
							>
								<Replace
									className={`h-4 w-4 ${
										account.requestTransformer ? "text-primary" : ""
									}`}
								/>
							</Button>
						)}
					{account.provider === "qwen" && onReauth && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onReauth(account)}
							title="Re-authenticate this Qwen account (preserves all metadata)"
						>
							<KeyRound className="h-4 w-4" />
						</Button>
					)}
					{account.provider === "anthropic" &&
						account.hasRefreshToken &&
						onAnthropicReauth && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onAnthropicReauth(account)}
								title="Re-authenticate this Anthropic account (preserves all metadata)"
							>
								<KeyRound className="h-4 w-4" />
							</Button>
						)}
					{account.provider === "codex" && onCodexReauth && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onCodexReauth(account)}
							title="Re-authenticate this Codex account (preserves all metadata)"
						>
							<KeyRound className="h-4 w-4" />
						</Button>
					)}
					{(account.provider === "anthropic" ||
						account.provider === "codex") && (
						<Button
							variant="ghost"
							size="sm"
							className="h-8 gap-1 text-xs"
							disabled={isRefreshingUsage}
							onClick={async () => {
								setIsRefreshingUsage(true);
								try {
									await onRefreshUsage(account);
								} finally {
									setIsRefreshingUsage(false);
								}
							}}
							title={
								account.provider === "codex"
									? "Refresh usage data (sends one minimal Codex request — consumes a small slice of quota)"
									: "Refresh usage data (restarts usage polling and refreshes token if expired)"
							}
						>
							<RefreshCw
								className={`h-3.5 w-3.5 ${isRefreshingUsage ? "animate-spin" : ""}`}
							/>
						</Button>
					)}
					{showForceReset && (
						<Button
							variant="outline"
							size="sm"
							className="h-8 gap-1 text-xs"
							onClick={() => onForceResetRateLimit(account)}
							title={
								staleLockDetected
									? "Reset stale rate limit lock (usage shows capacity available)"
									: "Force clear rate limit state from database"
							}
						>
							<RefreshCw className="h-3.5 w-3.5" />
							Force Reset
						</Button>
					)}
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onPauseToggle(account)}
						title={account.paused ? "Resume account" : "Pause account"}
					>
						{account.paused ? (
							<Play className="h-4 w-4" />
						) : (
							<Pause className="h-4 w-4" />
						)}
					</Button>
					<Button variant="ghost" size="sm" onClick={() => onRemove(account)}>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			</div>
			{account.sessionStats && (
				<div className="text-xs text-muted-foreground">
					Session: {account.sessionStats.requests} req
					{" · "}↑{formatTokenCount(account.sessionStats.inputTokens)} in
					{" · "}✦
					{formatTokenCount(account.sessionStats.cacheCreationInputTokens)}{" "}
					cache↑
					{" · "}✦{formatTokenCount(account.sessionStats.cacheReadInputTokens)}{" "}
					cache↓
					{" · "}↓{formatTokenCount(account.sessionStats.outputTokens)} out
					{account.sessionStats.planCostUsd > 0 && (
						<>
							{" · "}${account.sessionStats.planCostUsd.toFixed(2)} plan
						</>
					)}
					{account.sessionStats.apiCostUsd > 0 && (
						<>
							{" · "}${account.sessionStats.apiCostUsd.toFixed(2)} api
						</>
					)}
				</div>
			)}
			{(account.rateLimitReset ||
				account.usageData ||
				account.usageRateLimitedUntil ||
				// Codex quota data only arrives piggybacked on real traffic (no usage
				// polling endpoint), so gaps are routine. Mount the bar anyway: it
				// renders the weekly window as "Data unavailable" instead of
				// disappearing, and a missing quota bar reads as "no limit".
				account.provider === "codex" ||
				// DeepSeek has no usage data; mount for the peak/off-peak badge.
				account.provider === "deepseek" ||
				providerShowsCreditsBalance(account.provider)) && (
				<RateLimitProgress
					resetIso={account.rateLimitReset}
					usageUtilization={account.usageUtilization}
					usageWindow={account.usageWindow}
					usageData={account.usageData}
					usageRateLimitedUntil={account.usageRateLimitedUntil}
					usageThrottledUntil={account.usageThrottledUntil}
					usageThrottledWindows={account.usageThrottledWindows}
					provider={account.provider}
					peakHours={account.peakHours}
					showWeekly={providerShowsWeeklyUsage(account.provider)}
					pauseThresholdFiveHour={
						supportsUsagePauseThreshold(account.provider) &&
						account.usagePauseFiveHourEnabled
							? account.usagePauseFiveHourThreshold
							: null
					}
					pauseThresholdWeekly={
						supportsUsagePauseThreshold(account.provider) &&
						account.usagePauseWeeklyEnabled
							? account.usagePauseWeeklyThreshold
							: null
					}
				/>
			)}
		</div>
	);
}
